import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BuscarPecasRow } from '@core/types';
import Layout from '../componentes/Layout';
import BuscaPecas from '../componentes/BuscaPecas';
import Aviso from '../componentes/Aviso';
import { entradaMercadoria, fornecedores, ultimaCompraOuContagem } from '../lib/consultas';
import { contagemDoBalcaoSemEntrada } from '../lib/cadastroDoBalcao';
import { valorParaOPagamento } from '../lib/compraNaHora';
import { brl, dataBR, paraNumero, qtd } from '../lib/formato';
import { mensagemDeErro } from '../lib/supabase';

// Tela 6 — recebimento de mercadoria. Soma estoque e atualiza o custo numa
// transação só. O custo atualizado aqui é o que alimenta a margem calculada
// pela RPC margens(): entrada sem custo é margem cega depois.
//
// O PAGAMENTO SAIU DAQUI em 08/10/2026 (095). Havia um "Lançar saída no
// caixa" que somava quantidade × custo — e com o custo vazio (o dono confere
// custo só depois de cadastrar tudo) o total era zero e o banco não lançava
// nada, em silêncio. Pagamento na hora agora é "Paguei fornecedor", que não
// depende de custo e vira conta paga; esta tela leva para lá o fornecedor e o
// total, pelo ESTADO do roteador (nunca pela URL).

/** O que a tela do pagamento recebe daqui. */
interface ParaOPagamento {
  fornecedorId: string | null;
  valor: number | null;
  observacao: string;
}

interface ItemEntradaUI {
  product_id: string;
  sku: string;
  descricao: string;
  quantidade: number;
  custo_unit: number;
  atual: number;
  /** 093: a peça cadastrada pelo vendedor que ainda espera liberação. */
  conferencia: BuscarPecasRow['conferencia'];
}

export default function Entrada() {
  const cliente = useQueryClient();
  const [itens, setItens] = useState<ItemEntradaUI[]>([]);
  const [supplierId, setSupplierId] = useState('');
  const [observacao, setObservacao] = useState('');
  // A entrada que acabou de ser registrada: é dela que o "pagou agora?" leva
  // fornecedor, total e nota para a tela do pagamento.
  const [pronto, setPronto] = useState<ParaOPagamento | null>(null);

  const { data: listaFornecedores } = useQuery({
    queryKey: ['fornecedores'],
    queryFn: fornecedores,
    staleTime: 10 * 60_000,
  });

  const adicionar = (p: BuscarPecasRow) => {
    setPronto(null);
    setItens((atuais) => {
      if (atuais.some((i) => i.product_id === p.product_id)) return atuais;
      return [...atuais, {
        product_id: p.product_id,
        sku: p.sku,
        descricao: p.descricao,
        quantidade: 1,
        custo_unit: 0,
        atual: p.quantidade,
        conferencia: p.conferencia,
      }];
    });
  };

  const alterar = (id: string, campos: Partial<ItemEntradaUI>) =>
    setItens((atuais) => atuais.map((i) => (i.product_id === id ? { ...i, ...campos } : i)));

  const total = itens.reduce((s, i) => s + i.quantidade * i.custo_unit, 0);

  const registrar = useMutation({
    mutationFn: () => entradaMercadoria({
      itens: itens.map((i) => ({
        product_id: i.product_id,
        quantidade: i.quantidade,
        custo_unit: i.custo_unit > 0 ? i.custo_unit : undefined,
      })),
      supplierId: supplierId || null,
      // O pagamento não sai mais daqui (ver o cabeçalho): a RPC continua
      // aceitando, e o padrão dela é não lançar.
      gerarCaixa: false,
      observacao: observacao.trim() || null,
    }),
    onSuccess: () => {
      setPronto({
        fornecedorId: supplierId || null,
        // O total só vai quando TODA peça tem custo: com custo em parte das
        // linhas, ele é a soma de um pedaço (valorParaOPagamento).
        valor: valorParaOPagamento(itens),
        observacao: observacao.trim(),
      });
      setItens([]);
      setObservacao('');
      cliente.invalidateQueries({ queryKey: ['buscar'] });
      cliente.invalidateQueries({ queryKey: ['reposicao'] });
      cliente.invalidateQueries({ queryKey: ['caixa'] });
      // A entrada libera a peça pendente (093): a fila e o sino mudam junto.
      cliente.invalidateQueries({ queryKey: ['fila-conferencia'] });
      cliente.invalidateQueries({ queryKey: ['sino', 'para-liberar'] });
      // E encerra o aviso da contagem do balcão: agora houve nota.
      cliente.invalidateQueries({ queryKey: ['contagem-do-balcao'] });
    },
  });

  return (
    <Layout titulo="Entrada de mercadoria">
      <div className="space-y-5">
        {pronto && (
          <Aviso tipo="ok">
            Entrada registrada. Estoque somado e custos atualizados.{' '}
            <Link to="/compra-na-hora" state={pronto} className="font-bold underline">
              Pagou agora? Lançar o pagamento →
            </Link>
            {pronto.valor === null && (
              <span className="block text-xs">
                O valor pago você digita lá: nem toda peça tinha custo.
              </span>
            )}
          </Aviso>
        )}

        {/* O atalho do gerente (a Entrada é a tela dele na barra): pagar o
            fornecedor que está no balcão, sem bipar peça nem digitar custo. */}
        {!pronto && (
          <Link to="/compra-na-hora"
                className="cartao flex items-center justify-between gap-3 p-4 text-sm hover:border-signal">
            <span>Pagou o fornecedor agora, em dinheiro ou pix?</span>
            <span className="shrink-0 font-bold text-signal-soft">Lançar →</span>
          </Link>
        )}

        <BuscaPecas onEscolher={adicionar} placeholder="Bipe ou busque a peça recebida…" />

        {itens.length > 0 && (
          <>
            <section className="cartao p-4">
              <h2 className="rotulo">Itens recebidos</h2>
              <ul className="divide-y divide-edge">
                {itens.map((i) => (
                  <li key={i.product_id} className="py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate font-semibold">{i.descricao}</p>
                        <p className="font-mono text-xs text-dim">
                          {i.sku} · estoque atual {qtd(i.atual)}
                        </p>
                      </div>
                      <button
                        type="button"
                        className="shrink-0 px-2 py-1 text-sm text-erro"
                        onClick={() => setItens((a) => a.filter((x) => x.product_id !== i.product_id))}
                      >
                        remover
                      </button>
                    </div>

                    {/* Decisão do dono (08/10/2026): quem recebe a caixa com
                        nota já conferiu a peça — a entrada a LIBERA. Dito
                        antes do botão, para ninguém se surpreender depois. */}
                    {i.conferencia === 'pendente' && (
                      <div className="mt-2">
                        <Aviso tipo="alerta">
                          Esta peça foi cadastrada no balcão e espera liberação. Dar entrada
                          libera esta peça para vender.
                        </Aviso>
                      </div>
                    )}
                    {i.conferencia === 'habilitado' && <AvisoDaContagemDoBalcao productId={i.product_id} />}

                    <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-3">
                      <div>
                        <label className="rotulo" htmlFor={`q-${i.product_id}`}>Quantidade</label>
                        <input
                          id={`q-${i.product_id}`}
                          className="campo"
                          inputMode="decimal"
                          value={i.quantidade}
                          onChange={(e) => alterar(i.product_id, { quantidade: paraNumero(e.target.value) ?? 0 })}
                        />
                      </div>
                      <div>
                        <label className="rotulo" htmlFor={`c-${i.product_id}`}>Custo un. (R$)</label>
                        <input
                          id={`c-${i.product_id}`}
                          className="campo"
                          inputMode="decimal"
                          placeholder="0,00"
                          value={i.custo_unit === 0 ? '' : String(i.custo_unit)}
                          onChange={(e) => alterar(i.product_id, { custo_unit: paraNumero(e.target.value) ?? 0 })}
                        />
                      </div>
                      <p className="self-end pb-3 text-right font-bold sm:text-left">
                        {brl(i.quantidade * i.custo_unit)}
                      </p>
                    </div>

                    <p className="mt-1 text-xs text-dim">
                      Novo saldo: {qtd(i.atual + i.quantidade)}
                    </p>
                  </li>
                ))}
              </ul>
            </section>

            <section className="cartao space-y-4 p-4">
              <div>
                <label className="rotulo" htmlFor="fornecedor">Fornecedor</label>
                <select
                  id="fornecedor"
                  className="campo"
                  value={supplierId}
                  onChange={(e) => setSupplierId(e.target.value)}
                >
                  <option value="">Não informar</option>
                  {(listaFornecedores ?? []).map((f) => (
                    <option key={f.id} value={f.id}>{f.nome}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-dim">
                  Com fornecedor informado, o custo também atualiza a cotação dele — é o que o
                  roteamento de compras vai usar na Fase 2.
                </p>
              </div>

              <div>
                <label className="rotulo" htmlFor="obs-entrada">Observação (nota, pedido…)</label>
                <input
                  id="obs-entrada"
                  className="campo"
                  value={observacao}
                  onChange={(e) => setObservacao(e.target.value)}
                  placeholder="NF 12345 Skypeças"
                />
              </div>

              <p className="border border-edge p-3 text-xs text-dim">
                Pagou agora? Confirme a entrada e use o “Lançar o pagamento” que aparece em
                seguida — ele leva o fornecedor, e o total quando toda peça tem custo.
                Boleto a vencer vai em Contas a pagar.
              </p>

              <div className="flex items-baseline justify-between border-t border-edge pt-4">
                <span className="rotulo">Total da entrada</span>
                <span className="text-2xl font-black">{brl(total)}</span>
              </div>

              {registrar.error ? <Aviso tipo="erro">{mensagemDeErro(registrar.error)}</Aviso> : null}

              <button
                type="button"
                className="botao-primario w-full py-4"
                onClick={() => registrar.mutate()}
                disabled={registrar.isPending || itens.some((i) => i.quantidade <= 0)}
              >
                {registrar.isPending ? 'Registrando…' : 'Confirmar entrada'}
              </button>
            </section>
          </>
        )}
      </div>
    </Layout>
  );
}

/**
 * A NOTA DA MESMA CAIXA (093, achado da revisão de 09/10/2026). O vendedor
 * cadastra a peça a partir da caixa que acabou de chegar e declara 5; o gerente
 * libera em um toque e o saldo vai a 5; aí a nota da MESMA caixa entra aqui e
 * soma: 10. A entrada que vem antes da liberação não duplica (ela libera sem
 * lançar a contagem); a ordem inversa, que o sino favorece, sim.
 *
 * Só quem segura a caixa sabe se a nota é dela. Por isso a tela AVISA, e não
 * desconta sozinha: descontar uma caixa que era outra deixaria o estoque abaixo
 * da prateleira, e a peça "sem estoque" no balcão com ela ali. O aviso some na
 * primeira entrada (ou contagem da gerência) depois da contagem do balcão.
 */
export function AvisoDaContagemDoBalcao({ productId }: { productId: string }) {
  const { data } = useQuery({
    queryKey: ['contagem-do-balcao', productId],
    queryFn: () => ultimaCompraOuContagem(productId),
    staleTime: 30_000,
  });
  const contagem = contagemDoBalcaoSemEntrada(data);
  if (!contagem) return null;
  return (
    <div className="mt-2">
      <Aviso tipo="alerta">
        {contagem.origem === 'liberacao' ? 'Liberada' : 'Cadastrada'} em {dataBR(contagem.em)} com{' '}
        {qtd(contagem.quantidade)} un contadas no balcão, sem entrada depois disso. Se esta nota é da
        caixa que foi contada, essas unidades já estão no estoque e a entrada soma de novo: lance a
        nota e depois acerte o saldo em Ajustar estoque, na ficha da peça.
      </Aviso>
    </div>
  );
}
